import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useTranslation } from '@framework/i18n';
import { PaginationProps, TablePaginationConfig } from 'antd';
import { PaginationInfoWrapper } from '../table/TableData';

/**
 * restituisce le props della paginazione arricchite per l'accessibilità.
 *
 * @param showSizeChanger Di default è visualizzato impostare a false per nasconderlo.
 * @param hideTotal Di default è visualizzato il totale degli elementi (showTotal)
 * impostare hideTotal a true per nasconderlo.
 */
export const usePaginationA11y = (
  pagination: PaginationProps | false | TablePaginationConfig | undefined,
  hideTotal?: boolean,
) => {
  const { t } = useTranslation();
  const currentPageData = {
    current: pagination ? pagination?.current ?? 1 : 1,
    pageSize: pagination ? pagination?.pageSize ?? 10 : 10,
  };

  if (pagination === false) return false;

  const pageSizeOptions = pagination
    ? pagination?.pageSizeOptions ?? [5, 10, 20, 50]
    : [5, 10, 20, 50];

  const { showTotal, ...paginationProps } = pagination ?? {};

  const srAlertMessage = (
    <span className="sr-only" role="alert">
      {t(
        'use-pagination-a11y.current-page-sr-message',
        'Pagina corrente {{page}}.',
        {
          page: currentPageData.current,
        },
      )}
    </span>
  );

  const paginationA11y: TablePaginationConfig = {
    position: ['bottomRight'],
    size: 'small',
    'aria-label': `${t(
      'use-pagination-a11y.navigazione-pagine-pagina-corrente',
      'Navigazione pagine. Pagina corrente: {{current}}.',
      { current: currentPageData.current },
    )}`,
    // role: 'navigation',
    showSizeChanger: {
      'aria-label': `${t(
        'use-pagination-a11y.dimensioni-della-pagina',
        'Dimensioni della pagina. {{size}} elementi a pagina selezionato.',
        { size: currentPageData.pageSize },
      )}`,
      value: `${currentPageData.pageSize} / ${t(
        'use-pagination-a11y.pagina',
        'pagina',
      )}`,
      options: pageSizeOptions.map(size => ({
        value: size,
        label: (
          <>
            {size}{' '}
            <span aria-hidden="true">
              / {t('use-pagination-a11y.pagina', 'pagina')}
            </span>
            <span className="sr-only">
              {t('use-pagination-a11y.a-pagina', 'a pagina')}
            </span>
          </>
        ),
      })),
    },
    showTotal: (total, range) => {
      return hideTotal ? (
        <>{srAlertMessage}</>
      ) : (
        <PaginationInfoWrapper
          aria-label={`${t(
            'use-pagination-a11y.total-info-sr',
            'Da {{start}} a {{end}} di {{total}} elementi totali.',
            { start: range[0], end: range[1], total: total },
          )}`}
        >
          {range[0]}-{range[1]} {t('use-pagination-a11y.total-info.di', 'di')}{' '}
          {total}
          {srAlertMessage}
        </PaginationInfoWrapper>
      );
    },
    prevIcon: <LeftOutlined aria-hidden="true" />,
    nextIcon: <RightOutlined aria-hidden="true" />,
    jumpPrevIcon: <DoubleLeftOutlined aria-hidden="true" />,
    jumpNextIcon: <DoubleRightOutlined aria-hidden="true" />,
    itemRender: (page, type, originalElement) => {
      if (type === 'page') {
        return (
          <a
            rel="nofollow"
            aria-current={page === currentPageData.current ? 'page' : undefined}
            aria-disabled={
              page === currentPageData.current ? 'true' : undefined
            }
          >
            {page === currentPageData.current ? (
              <span
                aria-label={t(
                  'use-pagination-a11y.button.pagina-corrente',
                  'Pagina {{page}}, pagina corrente',
                  { page },
                )}
              >
                {page}
              </span>
            ) : (
              <span
                aria-label={t(
                  'use-pagination-a11y.button.vai-alla-pagina',
                  'Vai alla pagina {{page}}',
                  { page },
                )}
              >
                {page}
              </span>
            )}
          </a>
        );
      }
      return originalElement;
    },

    ...paginationProps,
  };

  return paginationA11y;
};
